import React, { useEffect } from 'react'

type PolicySection = {
  title: string
  paragraphs?: string[]
  bullets?: string[]
}

const EFFECTIVE_DATE = '5 August 2026'

const sections: PolicySection[] = [
  {
    title: '1. Introduction',
    paragraphs: [
      'LogiSelect Pty Ltd (ACN 700 403 345, ABN 62 700 403 345) ("LogiSelect", "we", "us" or "our") is committed to protecting the privacy of individuals who visit our website, use our transport management system (TMS), or otherwise interact with our services.',
      'This Privacy Policy explains what personal information we collect, how we use, disclose and store it, how you can access or correct it, and how you can contact us with a privacy question or complaint.',
      'We handle personal information in accordance with the Privacy Act 1988 (Cth) and the Australian Privacy Principles (APPs). Where we handle personal information of individuals located in the European Union or United Kingdom, we also comply with the General Data Protection Regulation (GDPR) and the UK GDPR.',
    ],
  },
  {
    title: '2. What personal information we collect',
    paragraphs: [
      'The types of personal information we collect depend on how you interact with us. In general, we may collect:',
    ],
    bullets: [
      'Contact details you submit — name, email address, company name, phone number, and the content of your message when you fill in a contact or enquiry form.',
      'Account details — for TMS users: username, hashed password, role, tenant assignment, and audit information about actions taken in the system.',
      'Technical and device information — IP address (stored as a salted SHA-256 hash, never in raw form), User-Agent string, browser, operating system, device type, screen and viewport size, language, timezone, and connection type.',
      'Usage information — pages visited, time on page, scroll depth, referrer URL, UTM campaign parameters, outbound link clicks, and interactions with forms.',
      'Cookies and similar identifiers — a first-party session identifier (`ls_sid`) and a consent-state cookie (`ls_consent`). We do not use third-party advertising trackers.',
      'Billing information for paying customers — company name, ABN, billing address, and invoice history. Payment card details are handled by our payment processor and are not stored on our systems.',
    ],
  },
  {
    title: '3. How we collect personal information',
    paragraphs: ['We collect personal information:'],
    bullets: [
      'Directly from you — when you submit a contact form, request a demo, register for the TMS, or communicate with us by email or phone.',
      'Automatically — when you visit our public website, information about your device and browsing activity is collected by our first-party analytics.',
      'From your organisation — if an authorised representative of your company creates a TMS account on your behalf.',
      'From third parties — such as public business registers (for ABN/ACN verification) and freight/logistics integration partners with whom you have chosen to connect.',
    ],
  },
  {
    title: '4. Why we collect and how we use personal information',
    paragraphs: ['We use personal information for the following purposes:'],
    bullets: [
      'To provide, operate and maintain the LogiSelect website and TMS.',
      'To respond to enquiries and contact requests you submit.',
      'To authenticate users, secure the TMS, and maintain audit logs.',
      'To measure aggregate usage of the public website and improve its content and performance.',
      'To send service-related communications (for example, security notices, incident reports, or billing).',
      'With your consent, to send marketing communications about LogiSelect products and updates. You can withdraw consent at any time.',
      'To comply with legal obligations, including record-keeping and responding to lawful requests by public authorities.',
      'To detect, prevent and respond to fraud, security incidents, or misuse of our services.',
    ],
  },
  {
    title: '5. Lawful basis for processing (EU/UK visitors)',
    paragraphs: [
      'If you are located in the European Union or United Kingdom, we rely on the following lawful bases under the GDPR / UK GDPR:',
    ],
    bullets: [
      'Consent — for the contact form and for optional marketing communications.',
      'Contract — to provide the LogiSelect TMS to you or your employer under our terms of service.',
      'Legitimate interests — for measuring aggregate website usage and for information security. We balance our interests against your rights and freedoms; you can object at any time.',
      'Legal obligation — for record-keeping and responding to lawful demands.',
    ],
  },
  {
    title: '6. Cookies and website analytics',
    paragraphs: [
      'Our public website uses first-party cookies and analytics only. We do not embed third-party advertising or social-media tracking scripts.',
      'Cookies we may set:',
    ],
    bullets: [
      '`ls_sid` — a random session identifier used to group page views into a visit. Retained for up to 180 days on your device.',
      '`ls_consent` — records your choice to accept or reject analytics. Retained for up to 365 days.',
      'Session cookies used by the TMS for authentication after you log in.',
    ],
  },
  {
    title: '7. Do Not Track and Global Privacy Control',
    paragraphs: [
      'We honour the "Do Not Track" (DNT) HTTP header and the Global Privacy Control (Sec-GPC) signal. If your browser sends either signal, our analytics client will not send events to our servers, regardless of the state of the on-screen consent banner.',
    ],
  },
  {
    title: '8. Disclosure of personal information',
    paragraphs: [
      'We do not sell your personal information. We may disclose personal information to:',
    ],
    bullets: [
      'Service providers that host our infrastructure, database, and email delivery — bound by written agreements to use the information only to provide services to us.',
      'Integration partners you or your organisation choose to connect to (for example, carriers, mapping providers, or ERP systems). We only share the data required for the integration to function.',
      'Professional advisers such as accountants, lawyers, and auditors, under confidentiality obligations.',
      'Law enforcement, regulators, or courts, when required or authorised by law.',
      'A successor entity in the event of a corporate reorganisation, merger or sale, in which case we will require the recipient to honour the commitments in this policy.',
    ],
  },
  {
    title: '9. Overseas disclosure',
    paragraphs: [
      'Our servers are located in Australia. Some of our service providers (for example, email or infrastructure providers) may store or process personal information in other countries, including the United States and the European Union.',
      'Before disclosing personal information overseas, we take reasonable steps to ensure the recipient handles it in a manner consistent with the Australian Privacy Principles and, where relevant, uses appropriate GDPR transfer mechanisms such as Standard Contractual Clauses.',
    ],
  },
  {
    title: '10. How we protect personal information',
    paragraphs: [
      'We implement technical and organisational measures appropriate to the sensitivity of the information we hold, including:',
    ],
    bullets: [
      'TLS/HTTPS encryption for all traffic to our websites and TMS.',
      'Salted, one-way hashing of visitor IP addresses so that the raw IP is never stored.',
      'Role-based access controls and least-privilege access to production data.',
      'Password hashing (bcrypt), JWT-based session tokens, and audit logging of privileged actions.',
      'Regular software updates, dependency vulnerability scanning, and backups.',
      'Employee confidentiality obligations and privacy training.',
    ],
    // Filler paragraph after bullets isn't rendered; keeping bullets self-contained.
  },
  {
    title: '11. Data retention',
    paragraphs: [
      'We keep personal information only for as long as necessary for the purposes for which it was collected, or as required by law.',
      'Typical retention periods:',
    ],
    bullets: [
      'Anonymous website analytics (session and event records): up to 90 days.',
      'Contact-form submissions and sales enquiries: up to 24 months, unless you request earlier deletion.',
      'TMS operational data (consignments, invoices, audit logs): for the duration of your subscription plus the period required by Australian tax and accounting law (typically 7 years).',
      'Backups: rotated on a rolling schedule; personal information in backups is deleted when backups are recycled.',
    ],
  },
  {
    title: '12. Your rights',
    paragraphs: ['Subject to applicable law, you have the right to:'],
    bullets: [
      'Access — request a copy of the personal information we hold about you.',
      'Correction — ask us to correct information that is inaccurate, out of date, incomplete, or misleading.',
      'Erasure — ask us to delete personal information we no longer need to keep. For public-website analytics, you can self-erase by clicking "Reject" in the consent banner or by using the erasure endpoint referenced below.',
      'Restriction and objection — ask us to stop or limit certain uses of your information, including direct marketing.',
      'Data portability (EU/UK visitors) — receive your personal information in a structured, commonly used, machine-readable format.',
      'Withdraw consent — where we rely on consent, you can withdraw it at any time without affecting the lawfulness of processing already carried out.',
      'Lodge a complaint — with us in the first instance, or with the Office of the Australian Information Commissioner (OAIC) or your local supervisory authority.',
    ],
  },
  {
    title: '13. How to make a privacy request',
    paragraphs: [
      'To exercise any of your rights, please contact us at info@logiselect.com.au with the subject line "Privacy request". We may need to verify your identity before we can act on your request.',
      'We will respond within a reasonable timeframe, and in any event within 30 days of receiving a valid request.',
      'To self-service delete website analytics data associated with your browser, you can either click "Reject" in the consent banner or send an authenticated request to our erasure endpoint at /api/v1/analytics/erase.',
    ],
  },
  {
    title: '14. Children\'s privacy',
    paragraphs: [
      'LogiSelect is a business-to-business service. We do not knowingly collect personal information from children under 16. If you believe we have inadvertently collected such information, please contact us and we will delete it.',
    ],
  },
  {
    title: '15. Third-party links',
    paragraphs: [
      'Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of those sites. We encourage you to read the privacy policy of every website you visit.',
    ],
  },
  {
    title: '16. Changes to this policy',
    paragraphs: [
      'We may update this Privacy Policy from time to time. When we do, we will post the updated version on this page and revise the "Effective Date" at the top. Where changes are material, we will take additional steps to notify you (for example, by an on-site notice or by email to registered users).',
    ],
  },
  {
    title: '17. How to contact us',
    paragraphs: [
      'If you have questions about this policy or the way we handle personal information, please contact:',
      'LogiSelect Pty Ltd',
      'ACN 700 403 345 | ABN 62 700 403 345',
      'Email: info@logiselect.com.au',
      'You can also contact the Office of the Australian Information Commissioner (OAIC) at www.oaic.gov.au if you are dissatisfied with our response to a privacy concern.',
    ],
  },
]

export default function PrivacyPolicyPage() {
  const WEBSITE_BASE_URL = String(import.meta.env.VITE_WEBSITE_BASE_URL || 'https://logiselect.com.au').replace(/\/$/, '')
  const TMS_BASE_URL = String(import.meta.env.VITE_TMS_BASE_URL || 'https://logiselect.net').replace(/\/$/, '')
  const tmsLoginUrl = `${TMS_BASE_URL}/login?autologin=1`
  const ownershipFooterText = '© 2026 LogiSelect Pty Ltd. All content on this website and all TMS software are owned by LogiSelect Pty Ltd.'

  useEffect(() => {
    const previousTitle = document.title
    document.title = 'Privacy Policy | LogiSelect Pty Ltd'
    return () => { document.title = previousTitle }
  }, [])

  const pageStyle: React.CSSProperties = {
    minHeight: '100vh',
    background: 'linear-gradient(180deg, #f6f7f3 0%, #ffffff 42%, #eef2f6 100%)',
    color: '#0f172a',
    position: 'relative',
  }

  const largeLogoPanel: React.CSSProperties = {
    position: 'absolute',
    left: 'max(18px, calc((100vw - 1200px) / 2 - 180px))',
    top: 18,
    width: 160,
    background: '#ffffff',
    border: '1px solid #dbe3ea',
    borderRadius: 20,
    boxShadow: '0 16px 34px rgba(15, 23, 42, 0.09)',
    padding: '22px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }

  const shellStyle: React.CSSProperties = {
    width: 'min(1200px, calc(100% - 32px))',
    margin: '0 auto',
    padding: '24px 0 48px',
  }

  const heroGrid: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1.4fr) minmax(320px, 420px)',
    gap: 28,
    alignItems: 'start',
  }

  const heroCard: React.CSSProperties = {
    background: 'radial-gradient(circle at top left, rgba(180, 213, 190, 0.55), rgba(255,255,255,0.96) 42%), linear-gradient(135deg, #fff 0%, #f3f6f8 100%)',
    border: '1px solid rgba(148, 163, 184, 0.25)',
    borderRadius: 28,
    padding: '36px 34px',
    boxShadow: '0 22px 50px rgba(15, 23, 42, 0.08)',
  }

  const sectionCard: React.CSSProperties = {
    background: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: 24,
    padding: '28px 28px 30px',
    boxShadow: '0 16px 34px rgba(15, 23, 42, 0.06)',
  }

  const sideCard: React.CSSProperties = {
    background: '#fff',
    borderRadius: 24,
    padding: '28px 28px',
    border: '1px solid #dbe3ea',
    boxShadow: '0 24px 50px rgba(15, 23, 42, 0.12)',
    alignSelf: 'start',
  }

  const ctaPrimary: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '13px 18px',
    borderRadius: 999,
    background: '#16324f',
    color: '#ffffff',
    textDecoration: 'none',
    fontWeight: 700,
    border: 'none',
    cursor: 'pointer',
    minWidth: 180,
  }

  return (
    <div style={pageStyle} className="pw-page">
      <div style={largeLogoPanel} className="pw-logo-panel">
        <img src="/logiselect-logo.png" alt="LogiSelect logo" style={{ width: '100%', height: 'auto', maxWidth: 250, display: 'block' }} />
      </div>
      <div style={shellStyle} className="pw-shell">
        <div style={{ marginBottom: 18 }}>
          <a href={WEBSITE_BASE_URL} style={{ color: '#16324f', textDecoration: 'underline', fontWeight: 700, fontSize: 14 }}>
            Back to main website
          </a>
        </div>
        <div style={heroGrid} className="pw-hero-grid">
          <div style={{ display: 'grid', gap: 24 }}>
            <section style={heroCard}>
              <h1 style={{ margin: 0, fontSize: 'clamp(2rem, 3.8vw, 3.6rem)', lineHeight: 1.02, letterSpacing: '-0.04em', maxWidth: 760 }}>
                LogiSelect Privacy Policy
              </h1>
              <p style={{ margin: '18px 0 0', fontSize: 18, lineHeight: 1.65, color: '#334155', maxWidth: 760 }}>
                Effective Date: {EFFECTIVE_DATE}
              </p>
              <p style={{ margin: '12px 0 0', fontSize: 16, lineHeight: 1.7, color: '#475569', maxWidth: 760 }}>
                This Privacy Policy explains how LogiSelect Pty Ltd collects, uses, discloses and protects personal information in connection with our public website and the LogiSelect transport management system (TMS).
              </p>
            </section>

            {sections.map((section) => (
              <section key={section.title} style={sectionCard}>
                <h2 style={{ margin: 0, fontSize: 28, letterSpacing: '-0.03em' }}>{section.title}</h2>
                {section.paragraphs?.map((paragraph, idx) => (
                  <p key={`${section.title}-p-${idx}`} style={{ margin: '16px 0 0', color: '#334155', lineHeight: 1.8, fontSize: 16 }}>
                    {paragraph}
                  </p>
                ))}
                {section.bullets?.length ? (
                  <ul style={{ margin: '16px 0 0 1.1rem', color: '#334155', lineHeight: 1.85, fontSize: 16 }}>
                    {section.bullets.map((bullet) => (
                      <li key={bullet} style={{ marginBottom: 6 }}>{bullet}</li>
                    ))}
                  </ul>
                ) : null}
              </section>
            ))}
          </div>

          <div style={{ display: 'grid', gap: 28, alignContent: 'start' }}>
            <section style={sideCard}>
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <img src="/logiselect-logo.png" alt="LogiSelect logo" style={{ height: 52, width: 'auto', display: 'inline-block', marginBottom: 8 }} />
                <h2 style={{ margin: 0, fontSize: 24, color: '#0f172a', fontWeight: 800 }}>LogiSelect TMS</h2>
                <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: 14 }}>Use the dedicated TMS link to sign in and work inside the system.</p>
              </div>
              <a href={tmsLoginUrl} style={{ ...ctaPrimary, width: '100%', boxSizing: 'border-box' }}>Open TMS</a>
            </section>

            <section style={sideCard}>
              <h3 style={{ margin: 0, fontSize: 24, color: '#0f172a', fontWeight: 800 }}>LogiSelect Pty Ltd</h3>
              <div style={{ marginTop: 14, color: '#334155', lineHeight: 1.75, fontSize: 18 }}>
                <div>ACN: 700 403 345</div>
                <div>ABN: 62 700 403 345</div>
                <div style={{ marginTop: 10 }}>
                  Privacy contact:{' '}
                  <a href="mailto:info@logiselect.com.au" style={{ color: '#16324f', fontWeight: 700 }}>
                    info@logiselect.com.au
                  </a>
                </div>
              </div>
            </section>

            <section style={sideCard}>
              <h3 style={{ margin: 0, fontSize: 20, color: '#0f172a', fontWeight: 800 }}>Related documents</h3>
              <ul style={{ margin: '12px 0 0 1.1rem', color: '#334155', lineHeight: 1.85, fontSize: 16 }}>
                <li><a href="/terms-and-conditions" style={{ color: '#16324f', fontWeight: 700 }}>Terms and Conditions</a></li>
                <li><a href="/api/v1/analytics/privacy" style={{ color: '#16324f', fontWeight: 700 }}>Machine-readable privacy notice</a></li>
              </ul>
            </section>
          </div>
        </div>

        <footer
          style={{
            marginTop: 28,
            textAlign: 'center',
            color: '#475569',
            fontSize: 13,
            lineHeight: 1.6,
            padding: '10px 8px 2px',
          }}
        >
          {ownershipFooterText}
        </footer>
      </div>
    </div>
  )
}
